import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CorrecaoTexto {
  offset: number;
  length: number;
  mensagem: string;
  sugestoes: string[];
  tipo: string | null;
}

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? "https://beta.crm-api.primetecia.com.br/api";

async function verificar(texto: string): Promise<CorrecaoTexto[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return [];
  const res = await fetch(`${BASE()}/v1/corretor/verificar`, {
    method: "POST",
    signal: AbortSignal.timeout(6000),
    headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ texto }),
  });
  if (!res.ok) return [];
  const json = await res.json().catch(() => ({}));
  return Array.isArray(json?.data) ? json.data : [];
}

export function useCorretorTexto(texto: string, ativo: boolean) {
  const [resultado, setResultado] = useState<{ texto: string; correcoes: CorrecaoTexto[] }>({ texto: "", correcoes: [] });
  const pedido = useRef(0);
  const aplicado = useRef(0);

  useEffect(() => {
    if (!ativo || texto.trim().length < 3) {
      setResultado({ texto, correcoes: [] });
      return;
    }
    const fimDePalavra = /[\s.,!?;:]$/.test(texto);
    const t = setTimeout(() => {
      const n = ++pedido.current;
      verificar(texto)
        .then(correcoes => {
          if (n < aplicado.current) return;
          aplicado.current = n;
          setResultado({ texto, correcoes });
        })
        .catch(() => undefined);
    }, fimDePalavra ? 0 : 350);
    return () => clearTimeout(t);
  }, [texto, ativo]);

  const correcoes = resultado.texto === texto
    ? resultado.correcoes
    : resultado.correcoes.filter(c =>
        c.offset + c.length <= texto.length &&
        texto.slice(c.offset, c.offset + c.length) === resultado.texto.slice(c.offset, c.offset + c.length) &&
        /[^\p{L}\p{N}]|^$/u.test(texto.charAt(c.offset + c.length)));

  const aplicar = (c: CorrecaoTexto, sugestao: string) =>
    texto.slice(0, c.offset) + sugestao + texto.slice(c.offset + c.length);

  return { correcoes, aplicar };
}
